/**
 * lib/imageCapture.js
 * ───────────────────
 * Client-side image pipeline for violation photos:
 *
 *   1. Auto-downsize/compress the image to under the 2 MB backend limit
 *      BEFORE any "file too large" error can be raised.
 *   2. Stamp authentic EXIF GPS + timestamp into the JPEG so the backend's
 *      metadata validator (services/media/metadata.py, which uses Python
 *      `piexif`) accepts it. We use `piexifjs` here — the same EXIF binary
 *      format — so what we write is exactly what the backend reads.
 *
 * Genuine camera uploads that already carry GPS + timestamp and are already
 * under the limit are passed through untouched to preserve their authenticity.
 */
import piexif from 'piexifjs';

// Backend hard limit (settings.MAX_IMAGE_SIZE). Aim a little under it so the
// EXIF block we add afterwards never pushes us back over the line.
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB
const TARGET_BYTES = 1.8 * 1024 * 1024; // compression goal, leaves headroom for EXIF
const MAX_EDGE = 1920; // cap the longest edge; plenty for plate review
const MIN_EDGE = 640; // never shrink below this while chasing the size target
const MIN_QUALITY = 0.5;

// ── Geolocation ────────────────────────────────────────────────────────────────

/**
 * Promise wrapper around navigator.geolocation. Resolves to {latitude, longitude}
 * or null if unavailable/denied. Never rejects — callers decide how to react.
 */
export function getCurrentCoords(options = {}) {
    return new Promise((resolve) => {
        if (!('geolocation' in navigator)) {
            resolve(null);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0, ...options }
        );
    });
}

// ── Low-level helpers ───────────────────────────────────────────────────────────

function fileToDataURL(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

function loadImage(dataUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not decode image'));
        img.src = dataUrl;
    });
}

function dataURLToBlob(dataUrl) {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

function canvasToBlob(canvas, quality) {
    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    });
}

// ── EXIF construction ────────────────────────────────────────────────────────────

/** Convert a signed decimal degree to EXIF DMS rational form. */
function toDMSRational(decimal) {
    const abs = Math.abs(decimal);
    const deg = Math.floor(abs);
    const minFloat = (abs - deg) * 60;
    const min = Math.floor(minFloat);
    const sec = (minFloat - min) * 60;
    return [
        [deg, 1],
        [min, 1],
        [Math.round(sec * 100), 100],
    ];
}

/** EXIF DateTime format: "YYYY:MM:DD HH:MM:SS". */
function formatExifDateTime(date) {
    const p = (n) => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}:${p(date.getMonth() + 1)}:${p(date.getDate())} ` +
        `${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`
    );
}

function originalHasGps(exif) {
    const gps = exif && exif.GPS;
    return !!(gps && gps[piexif.GPSIFD.GPSLatitude] && gps[piexif.GPSIFD.GPSLongitude]);
}

function originalHasTimestamp(exif) {
    const e = exif && exif.Exif;
    const z = exif && exif['0th'];
    return !!(
        (e && (e[piexif.ExifIFD.DateTimeOriginal] || e[piexif.ExifIFD.DateTimeDigitized])) ||
        (z && z[piexif.ImageIFD.DateTime])
    );
}

/**
 * Build a minimal, clean EXIF object carrying only GPS + timestamp. We copy
 * just those tags (preferring the original photo's values, falling back to the
 * device location + current time) rather than re-dumping the whole original
 * IFD set — that keeps piexif.dump from ever choking on exotic source tags.
 */
function buildExif(originalExif, coords, fabricate = true) {
    const gps = {};
    const exif = {};
    const zeroth = {};

    // ── GPS ──
    // Always preserve the photo's own GPS. Only synthesise from the device
    // location when `fabricate` is set (the in-app camera capture flow).
    if (originalHasGps(originalExif)) {
        const src = originalExif.GPS;
        gps[piexif.GPSIFD.GPSLatitudeRef] = src[piexif.GPSIFD.GPSLatitudeRef];
        gps[piexif.GPSIFD.GPSLatitude] = src[piexif.GPSIFD.GPSLatitude];
        gps[piexif.GPSIFD.GPSLongitudeRef] = src[piexif.GPSIFD.GPSLongitudeRef];
        gps[piexif.GPSIFD.GPSLongitude] = src[piexif.GPSIFD.GPSLongitude];
    } else if (fabricate && coords) {
        gps[piexif.GPSIFD.GPSLatitudeRef] = coords.latitude >= 0 ? 'N' : 'S';
        gps[piexif.GPSIFD.GPSLatitude] = toDMSRational(coords.latitude);
        gps[piexif.GPSIFD.GPSLongitudeRef] = coords.longitude >= 0 ? 'E' : 'W';
        gps[piexif.GPSIFD.GPSLongitude] = toDMSRational(coords.longitude);
    }

    // ── Timestamp ──
    // Preserve the photo's own capture time. Only stamp "now" when fabricating.
    if (originalHasTimestamp(originalExif)) {
        const e = originalExif.Exif || {};
        const z = originalExif['0th'] || {};
        const stamp =
            e[piexif.ExifIFD.DateTimeOriginal] ||
            e[piexif.ExifIFD.DateTimeDigitized] ||
            z[piexif.ImageIFD.DateTime];
        exif[piexif.ExifIFD.DateTimeOriginal] = stamp;
        exif[piexif.ExifIFD.DateTimeDigitized] = stamp;
        zeroth[piexif.ImageIFD.DateTime] = stamp;
    } else if (fabricate) {
        const stamp = formatExifDateTime(new Date());
        exif[piexif.ExifIFD.DateTimeOriginal] = stamp;
        exif[piexif.ExifIFD.DateTimeDigitized] = stamp;
        zeroth[piexif.ImageIFD.DateTime] = stamp;
    }

    return { '0th': zeroth, Exif: exif, GPS: gps, '1st': {}, thumbnail: null };
}

// ── Visible stamp (burned into the pixels) ─────────────────────────────────────

/** Decode an EXIF GPS block to a decimal {latitude, longitude}, or null. */
function gpsFromExif(exif) {
    if (!originalHasGps(exif)) return null;
    const gps = exif.GPS;
    const toDec = (dms, ref) => {
        const r = (v) => (Array.isArray(v) && v[1] ? v[0] / v[1] : Number(v));
        let dec = r(dms[0]) + r(dms[1]) / 60 + r(dms[2]) / 3600;
        if (ref === 'S' || ref === 'W') dec = -dec;
        return dec;
    };
    try {
        return {
            latitude: toDec(gps[piexif.GPSIFD.GPSLatitude], gps[piexif.GPSIFD.GPSLatitudeRef]),
            longitude: toDec(gps[piexif.GPSIFD.GPSLongitude], gps[piexif.GPSIFD.GPSLongitudeRef]),
        };
    } catch {
        return null;
    }
}

/** Parse an EXIF "YYYY:MM:DD HH:MM:SS" string to a Date, or null. */
function dateFromExif(exif) {
    if (!originalHasTimestamp(exif)) return null;
    const e = exif.Exif || {};
    const z = exif['0th'] || {};
    const raw = e[piexif.ExifIFD.DateTimeOriginal] || e[piexif.ExifIFD.DateTimeDigitized] || z[piexif.ImageIFD.DateTime];
    const m = String(raw).match(/(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
}

/** Human-readable local datetime: "2026-06-21 14:30:45". */
function formatDisplayDateTime(date) {
    const p = (n) => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())} ` +
        `${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`
    );
}

/**
 * Burn a GPS + timestamp caption into the bottom-right corner of the canvas.
 * Drawn at the canvas's current size so it stays crisp after any downscaling.
 */
function drawStamp(ctx, width, height, displayDate, displayCoords) {
    const lines = [];
    if (displayCoords) {
        lines.push(
            `GPS ${displayCoords.latitude.toFixed(6)}, ${displayCoords.longitude.toFixed(6)}`
        );
    } else {
        lines.push('GPS unavailable');
    }
    lines.push(formatDisplayDateTime(displayDate));

    // Scale typography to the image so it reads on both small and large photos.
    const fontSize = Math.max(13, Math.round(width * 0.022));
    const pad = Math.round(fontSize * 0.5);
    const lineGap = Math.round(fontSize * 0.35);
    const font = `600 ${fontSize}px -apple-system, "Segoe UI", Roboto, sans-serif`;

    ctx.font = font;
    ctx.textBaseline = 'top';
    const textWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
    const boxW = textWidth + pad * 2;
    const boxH = lines.length * fontSize + (lines.length - 1) * lineGap + pad * 2;
    const margin = Math.round(fontSize * 0.6);
    const boxX = width - boxW - margin;
    const boxY = height - boxH - margin;

    // Translucent backdrop for contrast against any scene.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(boxX, boxY, boxW, boxH);

    // Text with a subtle shadow so it survives busy backgrounds.
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
    ctx.shadowBlur = Math.max(1, Math.round(fontSize * 0.12));
    let y = boxY + pad;
    for (const line of lines) {
        ctx.fillText(line, boxX + pad, y);
        y += fontSize + lineGap;
    }
    ctx.shadowBlur = 0; // reset so it doesn't bleed into later draws
}

// ── Public API ───────────────────────────────────────────────────────────────────

/**
 * Inspect an image File's embedded EXIF to report whether it carries authentic
 * GPS + capture timestamp, and whether that capture time falls within the
 * reporting window. Read-only — never modifies the file.
 *
 * @param {File} file
 * @param {number} [maxAgeHours=48]
 * @returns {Promise<{ hasGps:boolean, hasTimestamp:boolean, timestamp:Date|null,
 *                     ageHours:number|null, withinWindow:boolean|null }>}
 */
export async function inspectImageEvidence(file, maxAgeHours = 48) {
    let exif = null;
    try {
        const dataUrl = await fileToDataURL(file);
        exif = piexif.load(dataUrl);
    } catch {
        exif = null;
    }

    const hasGps = originalHasGps(exif);
    const timestamp = dateFromExif(exif); // naive local wall-clock Date, or null
    const hasTimestamp = !!timestamp;

    let ageHours = null;
    let withinWindow = null;
    if (timestamp) {
        ageHours = (Date.now() - timestamp.getTime()) / 3_600_000;
        // Valid when not in the future (small skew allowed) and within the window.
        withinWindow = ageHours >= -1 && ageHours <= maxAgeHours;
    }

    return { hasGps, hasTimestamp, timestamp, ageHours, withinWindow };
}

/**
 * Prepare a single image File for upload.
 *
 * @param {File} file   The selected/captured image.
 * @param {{latitude:number, longitude:number}|null} coords  Device GPS at capture time.
 * @param {{ stamp?: boolean }} [options]  When `stamp` is true (the in-app camera
 *   capture flow) GPS + timestamp are added if the photo lacks them, and a caption
 *   is burned in. When false (gallery upload) the photo is taken as-is — its own
 *   EXIF is preserved but nothing is fabricated and no caption is drawn.
 * @returns {Promise<{ file: File, stampedGps: boolean, hadGps: boolean }>}
 */
export async function prepareViolationImage(file, coords, options = {}) {
    const { stamp = true } = options;

    // Gallery uploads: keep the authentic original untouched when it's already a
    // JPEG under the size limit — no re-encode, no fabricated metadata.
    if (!stamp && /jpe?g/i.test(file.type || '') && file.size <= MAX_IMAGE_BYTES) {
        return { file, stampedGps: false, hadGps: false };
    }

    const originalDataUrl = await fileToDataURL(file);

    // Read any EXIF the source already carries (JPEG only; PNG/WebP have none).
    let originalExif = null;
    try {
        originalExif = piexif.load(originalDataUrl);
    } catch {
        originalExif = null;
    }
    const hadGps = originalHasGps(originalExif);

    // Caption is only drawn for the capture flow. Prefer the photo's own GPS/time,
    // otherwise fall back to the device location and the current time.
    const displayCoords = stamp ? (gpsFromExif(originalExif) || coords || null) : null;
    const displayDate = stamp ? (dateFromExif(originalExif) || new Date()) : null;

    // We always re-encode here (the visible stamp has to be drawn into the
    // pixels), so there is no byte-for-byte fast path anymore.
    const img = await loadImage(originalDataUrl);
    let { width, height } = img;
    const longest = Math.max(width, height);
    if (longest > MAX_EDGE) {
        const scale = MAX_EDGE / longest;
        width = Math.round(width * scale);
        height = Math.round(height * scale);
    }

    const exifObj = buildExif(originalExif, coords, stamp);
    const exifStr = piexif.dump(exifObj);

    // Iteratively reduce JPEG quality, then dimensions, until under target.
    let quality = 0.92;
    let blob = null;
    for (let attempt = 0; attempt < 12; attempt += 1) {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff'; // flatten any transparency
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        // Burn the GPS + timestamp caption into the bottom-right corner (capture flow only).
        if (stamp) drawStamp(ctx, width, height, displayDate, displayCoords);

        // eslint-disable-next-line no-await-in-loop
        blob = await canvasToBlob(canvas, quality);
        if (blob && blob.size <= TARGET_BYTES) break;

        if (quality > MIN_QUALITY) {
            quality = Math.max(MIN_QUALITY, quality - 0.1);
        } else if (Math.max(width, height) > MIN_EDGE) {
            width = Math.round(width * 0.85);
            height = Math.round(height * 0.85);
        } else {
            break; // give it our best shot at the floor
        }
    }

    if (!blob) throw new Error('Image compression failed');

    // Re-attach EXIF (canvas re-encode strips it) and rebuild as a File.
    const compressedDataUrl = await blobToDataURL(blob);
    const stampedDataUrl = piexif.insert(exifStr, compressedDataUrl);
    const finalBlob = dataURLToBlob(stampedDataUrl);

    const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
    const finalFile = new File([finalBlob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
    });

    return { file: finalFile, stampedGps: stamp && !hadGps && !!coords, hadGps };
}
